import React from 'react';
import { Plus, Search } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export const EditTransactionDialog = ({
  editingTransaction, editData, setEditData, isPersonal,
  accountSearchOpen, setAccountSearchOpen, accountSearch, setAccountSearch,
  filteredAccounts, allFilteredAccounts, getSelectedAccountName,
  onSave, onClose, onNewAccount,
}) => {
  if (!editingTransaction) return null;

  return (
    <Dialog open={!!editingTransaction} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Transaction</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Transaction description"
              data-testid="edit-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input
                type="number" step="0.01"
                value={editData.amount}
                onChange={(e) => setEditData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                className="font-mono"
                data-testid="edit-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input
                type="date" value={editData.date}
                onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
                data-testid="edit-date-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Account Category</Label>
              <Button type="button" variant="ghost" size="sm" onClick={onNewAccount} className="text-[#D94E18] h-auto py-1 px-2" data-testid="add-new-account-btn">
                <Plus className="w-3 h-3 mr-1" />Add New
              </Button>
            </div>
            
            <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={accountSearchOpen} className="w-full justify-between font-normal" data-testid="account-select-trigger">
                  {getSelectedAccountName()}
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[350px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search accounts..." value={accountSearch} onValueChange={setAccountSearch} data-testid="account-search-input" />
                  <CommandList>
                    <CommandEmpty>
                      <div className="py-4 text-center">
                        <p className="text-sm text-zinc-500">No accounts found.</p>
                        <Button variant="link" size="sm" onClick={() => { setAccountSearchOpen(false); onNewAccount(); }} className="text-[#D94E18]">
                          Create new account
                        </Button>
                      </div>
                    </CommandEmpty>
                    {filteredAccounts.length > 0 && (
                      <CommandGroup heading={editingTransaction.amount < 0 ? "Expense Accounts" : "Revenue Accounts"}>
                        {filteredAccounts.slice(0, 10).map((account) => (
                          <CommandItem
                            key={account.id}
                            value={`${account.code} ${account.name}`}
                            onSelect={() => { setEditData(prev => ({ ...prev, suggested_account_id: account.id })); setAccountSearchOpen(false); setAccountSearch(''); }}
                            data-testid={`account-option-${account.id}`}
                          >
                            <span className="font-mono text-zinc-500 mr-2">{account.code}</span>{account.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {allFilteredAccounts.filter(a => !filteredAccounts.find(fa => fa.id === a.id)).length > 0 && (
                      <CommandGroup heading="All Accounts (incl. Balance Sheet)">
                        {allFilteredAccounts.filter(a => !filteredAccounts.find(fa => fa.id === a.id)).slice(0, 15).map((account) => (
                          <CommandItem
                            key={account.id}
                            value={`${account.code} ${account.name}`}
                            onSelect={() => { setEditData(prev => ({ ...prev, suggested_account_id: account.id })); setAccountSearchOpen(false); setAccountSearch(''); }}
                            data-testid={`account-option-other-${account.id}`}
                          >
                            <span className="font-mono text-zinc-500 mr-2">{account.code}</span>{account.name}
                            <Badge variant="outline" className="ml-2 text-xs capitalize">{account.account_type}</Badge>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Input value={editData.notes} onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Add notes..." data-testid="notes-input" />
          </div>

          {!isPersonal && editingTransaction?.tax_rate !== undefined && editingTransaction?.tax_rate !== null && (
            <div className="space-y-1.5">
              <Label>Tax / VAT Rate (%)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  value={editData.is_tax_exempt ? '' : (editData.tax_rate ?? '')}
                  onChange={(e) => setEditData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0, is_tax_exempt: false }))}
                  disabled={editData.is_tax_exempt}
                  className="w-24 font-mono"
                  data-testid="tax-rate-input"
                />
                <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input
                    type="checkbox" checked={editData.is_tax_exempt}
                    onChange={(e) => setEditData(prev => ({ ...prev, is_tax_exempt: e.target.checked, tax_rate: e.target.checked ? 0 : prev.tax_rate }))}
                    className="rounded border-zinc-300"
                    data-testid="tax-exempt-checkbox"
                  />
                  Tax exempt
                </label>
              </div>
              {!editData.is_tax_exempt && editData.tax_rate > 0 && (
                <p className="text-xs text-zinc-400 font-mono">
                  Net: {formatCurrency(Math.abs(editData.amount) / (1 + editData.tax_rate / 100), editingTransaction.currency)}
                  {' + '}Tax: {formatCurrency(Math.abs(editData.amount) - Math.abs(editData.amount) / (1 + editData.tax_rate / 100), editingTransaction.currency)}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} className="bg-[#D94E18] hover:bg-[#C2410C]" data-testid="save-edit-btn">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
